import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserPlus, UserCog, UserMinus, ShieldCheck, KeyRound, IdCard, LayoutDashboard,
  Sparkles, Loader2, CheckCircle2, XCircle, MinusCircle, AlertTriangle, Plug, Cloud,
} from "lucide-react";

const STEP_ICON = {
  SUCCESS: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  SKIPPED: <MinusCircle className="h-4 w-4 text-slate-400" />,
  FAILED: <XCircle className="h-4 w-4 text-red-600" />,
  RUNNING: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
  PENDING: <div className="h-2.5 w-2.5 rounded-full bg-slate-300 mx-0.5" />,
  SETUP_REQUIRED: <Cloud className="h-4 w-4 text-amber-500" />,
};
const STATUS_CLS = {
  COMPLETE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  IN_PROGRESS: "bg-blue-50 text-blue-700 border-blue-200",
  PENDING: "bg-slate-100 text-slate-600 border-slate-200",
  PENDING_REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
  ACTION_REQUIRED: "bg-red-50 text-red-700 border-red-200",
  GRANTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDING_APPROVAL: "bg-amber-50 text-amber-700 border-amber-200",
  DENIED: "bg-red-50 text-red-700 border-red-200",
  EXPIRED: "bg-slate-100 text-slate-500 border-slate-200",
  SETUP_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
  CONNECTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const lbl = (s) => (s || "").replace(/_/g, " ");
const chip = (s) => "text-[10px] border " + (STATUS_CLS[s] || STATUS_CLS.PENDING);

const TABS = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["joiners", "Joiners", UserPlus],
  ["movers", "Movers", UserCog],
  ["leavers", "Leavers", UserMinus],
  ["privileged", "Privileged", KeyRound],
  ["licences", "M365 Licences", IdCard],
  ["microsoft", "Microsoft", Plug],
  ["personas", "Personas", ShieldCheck],
];

function Field({ label, children }) {
  return <div><div className="text-xs text-slate-500 mb-1">{label}</div>{children}</div>;
}

/* ---------------- Workflow list + step tracker */
function WorkflowList({ type, personas, refresh, reloadKey }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => api.get("/identity-access/workflows", { params: { type } })
    .then((r) => setItems(r.data.items)).catch(() => {}), [type]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, reloadKey, load]);

  const advance = async (wid, key, result) => {
    let note;
    if (result === "SKIPPED") { note = window.prompt("Reason to skip this step?") || "skipped"; }
    try {
      await api.post(`/identity-access/workflows/${wid}/step/${key}`, { result, note });
      const d = await api.get(`/identity-access/workflows/${wid}`);
      setOpen(d.data); load(); refresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Step failed"); }
  };

  return (
    <div className="space-y-2" data-testid={`wf-list-${type}`}>
      {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No {type.toLowerCase()} workflows yet.</div>}
      {items.map((w) => (
        <button key={w.id} onClick={() => api.get(`/identity-access/workflows/${w.id}`).then((r) => setOpen(r.data))}
          data-testid={`wf-${w.id}`}
          className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 text-left">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-800 truncate">{w.full_name}</div>
            <div className="text-[11px] text-slate-400 truncate">
              {w.department || w.employee_email || "—"}{w.persona_name ? ` · ${w.persona_name}` : ""}
              {w.proposed_identity ? ` · ${w.proposed_identity.upn}` : ""}
            </div>
          </div>
          <Badge className={chip(w.status)}>{lbl(w.status)}</Badge>
        </button>
      ))}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wf-detail">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {open.full_name}
                  <Badge className={chip(open.status)}>{lbl(open.status)}</Badge>
                </DialogTitle>
              </DialogHeader>
              {open.type === "MOVER" ? (
                <div className="space-y-3 text-sm">
                  <div className="text-slate-500">{open.current_persona || "—"} → <strong>{open.new_persona}</strong></div>
                  {["add", "remove", "keep"].map((k) => (
                    <div key={k}>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                        {k === "add" ? "Add" : k === "remove" ? "Remove (excess access review)" : "Keep"}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(open.diff?.[k] || []).length === 0 ? <span className="text-slate-400 text-xs">—</span>
                          : open.diff[k].map((x) => (
                            <Badge key={x} className={"text-[10px] border " + (k === "add"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : k === "remove" ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-slate-100 text-slate-600 border-slate-200")}>{x}</Badge>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(open.licence_recovery?.assigned?.length > 0) && (
                    <div className="mb-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5" /> Licence recovery: {open.licence_recovery.assigned.join(", ")}
                    </div>
                  )}
                  {(open.steps || []).map((s) => (
                    <div key={s.key} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2"
                      data-testid={`step-${s.key}`}>
                      <div className="w-5 grid place-items-center">{STEP_ICON[s.state]}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-700 truncate">{s.label}</div>
                        {s.state === "SETUP_REQUIRED" && <div className="text-[10px] text-amber-600">Needs Microsoft Graph</div>}
                        {s.note && <div className="text-[10px] text-slate-400">{s.note}</div>}
                      </div>
                      {s.state !== "SUCCESS" && s.state !== "SETUP_REQUIRED" && (
                        <div className="flex gap-1">
                          <button onClick={() => advance(open.id, s.key, "SUCCESS")} title="Done"
                            data-testid={`done-${s.key}`} className="text-emerald-600 hover:bg-emerald-50 rounded p-1">
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => advance(open.id, s.key, "SKIPPED")} title="Skip"
                            className="text-slate-400 hover:bg-slate-50 rounded p-1"><MinusCircle className="h-4 w-4" /></button>
                          <button onClick={() => advance(open.id, s.key, "FAILED")} title="Failed"
                            className="text-red-500 hover:bg-red-50 rounded p-1"><XCircle className="h-4 w-4" /></button>
                        </div>
                      )}
                      {s.state === "SETUP_REQUIRED" && (
                        <button onClick={() => advance(open.id, s.key, "SKIPPED")} title="Skip"
                          className="text-slate-400 hover:bg-slate-50 rounded p-1"><MinusCircle className="h-4 w-4" /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function IdentityAccess() {
  const [tab, setTab] = useState("dashboard");
  const [personas, setPersonas] = useState([]);
  const [dash, setDash] = useState(null);
  const [status, setStatus] = useState(null);
  const [lic, setLic] = useState(null);
  const [priv, setPriv] = useState(null);
  const [ai, setAi] = useState(""); const [aiAns, setAiAns] = useState(null); const [aiBusy, setAiBusy] = useState(false);
  const [reload, setReload] = useState(0);

  const refresh = () => setReload((r) => r + 1);
  useEffect(() => {
    api.get("/identity-access/personas").then((r) => setPersonas(r.data.items)).catch(() => {});
    api.get("/identity-access/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/identity-access/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/identity-access/licences").then((r) => setLic(r.data)).catch(() => {});
    api.get("/identity-access/privileged").then((r) => setPriv(r.data)).catch(() => {});
  }, [reload]);

  const askAi = async () => {
    if (!ai.trim()) return;
    setAiBusy(true); setAiAns(null);
    try { const { data } = await api.post("/identity-access/ai", { query: ai.trim() }); setAiAns(data); }
    catch { toast.error("ONE AI unavailable"); } finally { setAiBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-5 space-y-4" data-testid="identity-access-page">
      <div className="flex items-center gap-2">
        <IdCard className="h-6 w-6 text-slate-800" />
        <div>
          <h1 className="text-xl font-bold text-slate-900">Identity & Access Governance</h1>
          <p className="text-sm text-slate-500">Joiner · Mover · Leaver · Licences · Privileged access — on ONE data.</p>
        </div>
        {status && (
          <Badge className={"ml-auto " + chip(status.graph_connected ? "CONNECTED" : "SETUP_REQUIRED")}
            data-testid="graph-badge">
            Microsoft Graph: {status.graph_connected ? "Connected" : "Setup Required"}
          </Badge>
        )}
      </div>

      {/* tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={"flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px " + (tab === id
              ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {tab === "dashboard" && dash && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="ia-dashboard">
            {[
              ["Joiners in progress", dash.cards.joiners_in_progress, "text-blue-600"],
              ["Movers pending", dash.cards.movers_pending, "text-amber-600"],
              ["Leavers in progress", dash.cards.leavers_in_progress, "text-blue-600"],
              ["Offboarding exceptions", dash.cards.offboarding_exceptions, "text-red-600"],
              ["Privileged active", dash.cards.privileged_active, "text-emerald-600"],
              ["Licence recovery", dash.cards.licence_recovery, "text-amber-600"],
            ].map(([l, v, c]) => (
              <div key={l} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                <div className={"text-2xl font-bold " + c}>{v}</div>
                <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{l}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4" data-testid="ia-ai">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-semibold text-slate-800">Ask ONE AI</span></div>
            <div className="flex gap-2">
              <Input value={ai} onChange={(e) => setAi(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAi()}
                data-testid="ai-input" placeholder="Which leavers still have access?" className="h-9" />
              <Button size="sm" onClick={askAi} disabled={aiBusy} data-testid="ai-ask"
                className="bg-violet-600 hover:bg-violet-700 text-white">
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask"}</Button>
            </div>
            {aiAns && <div className="mt-3 text-sm text-slate-700 bg-white rounded-lg border border-violet-100 p-3" data-testid="ai-answer">
              {aiAns.answer}
              {(aiAns.rows || []).length > 0 && <ul className="mt-1.5 text-[12px] text-slate-500 space-y-0.5">
                {aiAns.rows.slice(0, 6).map((r, i) => <li key={i}>• {r.employee || r.email || r.licence}</li>)}</ul>}
            </div>}
          </div>
        </div>
      )}

      {/* JOINERS */}
      {tab === "joiners" && (
        <div className="space-y-3">
          <JoinerDialog personas={personas} onDone={refresh} />
          <WorkflowList type="JOINER" personas={personas} refresh={refresh} reloadKey={reload} />
        </div>
      )}
      {/* MOVERS */}
      {tab === "movers" && (
        <div className="space-y-3">
          <MoverDialog personas={personas} onDone={refresh} />
          <WorkflowList type="MOVER" personas={personas} refresh={refresh} reloadKey={reload} />
        </div>
      )}
      {/* LEAVERS */}
      {tab === "leavers" && (
        <div className="space-y-3">
          <LeaverDialog onDone={refresh} />
          <WorkflowList type="LEAVER" personas={personas} refresh={refresh} reloadKey={reload} />
        </div>
      )}

      {/* PRIVILEGED */}
      {tab === "privileged" && priv && (
        <div className="space-y-3">
          <PrivDialog onDone={refresh} />
          <div className="space-y-2" data-testid="priv-list">
            {priv.items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No privileged access requests.</div>}
            {priv.items.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`priv-${p.id}`}>
                <KeyRound className="h-4 w-4 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 truncate">{p.employee_email} · {p.ttl_hours}h</div>
                  <div className="text-[11px] text-slate-400 truncate">{p.reason}{p.expires_at ? ` · expires ${new Date(p.expires_at).toLocaleString()}` : ""}</div>
                </div>
                {p.status === "PENDING_APPROVAL" ? (
                  <div className="flex gap-1">
                    <Button size="sm" data-testid={`approve-${p.id}`}
                      onClick={() => api.post(`/identity-access/privileged/${p.id}/decision`, { approve: true }).then(refresh)}
                      className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
                    <Button size="sm" variant="outline" className="h-7"
                      onClick={() => api.post(`/identity-access/privileged/${p.id}/decision`, { approve: false }).then(refresh)}>Deny</Button>
                  </div>
                ) : <Badge className={chip(p.status)}>{lbl(p.status)}</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LICENCES */}
      {tab === "licences" && lic && (
        <div className="space-y-3" data-testid="lic-panel">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2">
            <Cloud className="h-4 w-4" /> <span>{lic.inventory_note}</span>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
              Licence recovery opportunities</div>
            {lic.recovery_opportunities.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400" data-testid="lic-empty">No recovery opportunities.</div>
            ) : lic.recovery_opportunities.map((r, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 text-sm" data-testid={`lic-${i}`}>
                <IdCard className="h-4 w-4 text-slate-400" />
                <span className="flex-1 text-slate-700">{r.employee} <span className="text-slate-400">· {r.email}</span></span>
                <Badge className="text-[10px] bg-slate-100 text-slate-600 border-slate-200 border">{r.licence}</Badge>
                <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 border">Former employee</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MICROSOFT */}
      {tab === "microsoft" && status && (
        <div className="space-y-2" data-testid="ms-panel">
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">{status.note}</div>
          {status.connectors.map((c) => (
            <div key={c.code} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid={`ms-${c.code}`}>
              <Plug className="h-4 w-4 text-slate-400" />
              <span className="flex-1 text-sm text-slate-700">{c.label}</span>
              <Badge className={chip(c.status)}>{lbl(c.status)}</Badge>
            </div>
          ))}
        </div>
      )}

      {/* PERSONAS */}
      {tab === "personas" && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="persona-panel">
          {personas.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`persona-${p.code}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-sm font-semibold text-slate-800">{p.name}</div>
                <Badge className="text-[10px] bg-slate-100 text-slate-600 border-slate-200 border">{p.licence}</Badge>
              </div>
              <div className="text-[11px] text-slate-500 space-y-0.5">
                <div><span className="text-slate-400">Groups:</span> {(p.groups || []).join(", ") || "—"}</div>
                <div><span className="text-slate-400">Apps:</span> {(p.applications || []).join(", ") || "—"}</div>
                <div><span className="text-slate-400">Device:</span> {p.device_profile || "—"}</div>
                <div><span className="text-slate-400">Security:</span> {p.security_policy || "—"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Create dialogs */
function JoinerDialog({ personas, onDone }) {
  const [o, setO] = useState(false);
  const [f, setF] = useState({ full_name: "", department: "", designation: "", persona_id: "" });
  const submit = async () => {
    if (!f.full_name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/identity-access/joiner", { ...f, persona_id: f.persona_id || null });
      toast.success("Joiner plan created"); setO(false); setF({ full_name: "", department: "", designation: "", persona_id: "" }); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={o} onOpenChange={setO}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-joiner" className="bg-slate-900 hover:bg-slate-800 text-white"><UserPlus className="h-4 w-4 mr-1" /> New joiner</Button></DialogTrigger>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>New joiner</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Full name"><Input data-testid="joiner-name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Department"><Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} /></Field>
            <Field label="Designation"><Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} /></Field>
          </div>
          <Field label="Persona">
            <Select value={f.persona_id} onValueChange={(v) => setF({ ...f, persona_id: v })}>
              <SelectTrigger data-testid="joiner-persona"><SelectValue placeholder="Select persona" /></SelectTrigger>
              <SelectContent>{personas.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Button onClick={submit} data-testid="joiner-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white">Create joiner plan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MoverDialog({ personas, onDone }) {
  const [o, setO] = useState(false);
  const [f, setF] = useState({ employee_email: "", new_persona_id: "", new_department: "" });
  const submit = async () => {
    if (!f.employee_email.trim() || !f.new_persona_id) { toast.error("Email + new persona required"); return; }
    try { await api.post("/identity-access/mover", f); toast.success("Mover review created"); setO(false); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={o} onOpenChange={setO}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-mover" className="bg-slate-900 hover:bg-slate-800 text-white"><UserCog className="h-4 w-4 mr-1" /> New mover</Button></DialogTrigger>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Role change (mover)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Employee email"><Input data-testid="mover-email" value={f.employee_email} onChange={(e) => setF({ ...f, employee_email: e.target.value })} /></Field>
          <Field label="New persona">
            <Select value={f.new_persona_id} onValueChange={(v) => setF({ ...f, new_persona_id: v })}>
              <SelectTrigger data-testid="mover-persona"><SelectValue placeholder="Select persona" /></SelectTrigger>
              <SelectContent>{personas.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="New department"><Input value={f.new_department} onChange={(e) => setF({ ...f, new_department: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="mover-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white">Compare access</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LeaverDialog({ onDone }) {
  const [o, setO] = useState(false);
  const [f, setF] = useState({ employee_email: "", reason: "" });
  const submit = async () => {
    if (!f.employee_email.trim()) { toast.error("Email required"); return; }
    try { await api.post("/identity-access/leaver", f); toast.success("Offboarding started"); setO(false); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={o} onOpenChange={setO}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-leaver" className="bg-slate-900 hover:bg-slate-800 text-white"><UserMinus className="h-4 w-4 mr-1" /> New leaver</Button></DialogTrigger>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Offboarding (leaver)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Employee email"><Input data-testid="leaver-email" value={f.employee_email} onChange={(e) => setF({ ...f, employee_email: e.target.value })} /></Field>
          <Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
          <p className="text-[11px] text-slate-400">ONE disables first (never deletes evidence early), then tracks each step independently.</p>
          <Button onClick={submit} data-testid="leaver-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white">Start offboarding</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrivDialog({ onDone }) {
  const [o, setO] = useState(false);
  const [f, setF] = useState({ employee_email: "", device: "", ttl_hours: "2", reason: "", ticket: "" });
  const submit = async () => {
    if (!f.employee_email.trim() || !f.reason.trim()) { toast.error("Email + reason required"); return; }
    try { await api.post("/identity-access/privileged", { ...f, ttl_hours: Number(f.ttl_hours) }); toast.success("Requested — pending approval"); setO(false); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={o} onOpenChange={setO}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-priv" className="bg-slate-900 hover:bg-slate-800 text-white"><KeyRound className="h-4 w-4 mr-1" /> Request temp admin</Button></DialogTrigger>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Temporary admin access</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Employee email"><Input data-testid="priv-email" value={f.employee_email} onChange={(e) => setF({ ...f, employee_email: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Device (optional)"><Input value={f.device} onChange={(e) => setF({ ...f, device: e.target.value })} /></Field>
            <Field label="Duration">
              <Select value={f.ttl_hours} onValueChange={(v) => setF({ ...f, ttl_hours: v })}>
                <SelectTrigger data-testid="priv-ttl"><SelectValue /></SelectTrigger>
                <SelectContent>{["1", "2", "4", "8"].map((h) => <SelectItem key={h} value={h}>{h} hour{h !== "1" ? "s" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Reason"><Input data-testid="priv-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
          <Field label="Ticket (if policy requires)"><Input value={f.ticket} onChange={(e) => setF({ ...f, ticket: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="priv-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white">Request (time-bound, audited)</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
